import { Link } from '@/components/ui/link';
import { cn } from '@/lib/utils';
import type { FormContent } from '@/lib/utils/form-data';
import { Document } from '@contentful/rich-text-types';
import HtmlEmbed from '@/components/ui/media/html-embed-client';
import { Image } from '../image/image';
import RichTextViewer from '../rich-text-viewer/rich-text-viewer';
import styles from './card-with-image.module.scss';
import LottieComponent from './lottie-component';
import RiveComponent from './rive-component';

export type CardWithImageProps = {
  title: string;
  titleTag?: 'P' | 'H2' | 'H3' | 'H4' | 'H5' | 'H6';
  titleStyle?: 'H2' | 'H3' | 'H4' | 'H5' | 'H6';
  description: string | Document;
  link: string;
  linkLabel?: string;
  newWindow?: boolean;
  type: 'link' | 'button';
  destination?: 'URL' | 'Page' | 'Form';
  ctaId?: string;
  formContent?: FormContent;
  cardCount: number;
  shouldCenterOnTablet?: boolean;
  centered?: boolean;
  centeredMobile?: boolean;
  imageType?: 'icon' | 'icon-lg' | 'image';
  contentDirection?: 'row' | 'column';
  imagePosition?: 'left' | 'right';
  image?: string;
  lottieJson?: string;
  videoEmbedUrl?: string;
  embedCode?: string;
  bgVariant?: 'white' | 'grey' | 'violet' | 'blue';
  buttonStyle?: 'Pink' | 'White';
  listStyle?: 'default' | 'checkboxes';
  mobileContentFirst?: boolean;
  carouselMode?: boolean;
  fullWidthImage?: boolean;
};

export const CardWithImage = ({
  title,
  titleTag,
  titleStyle,
  description,
  cardCount,
  shouldCenterOnTablet = false,
  link,
  linkLabel,
  newWindow,
  image,
  type = 'link',
  destination,
  ctaId,
  formContent,
  centered = false,
  centeredMobile,
  imageType = 'icon',
  contentDirection = 'column',
  imagePosition = 'left',
  bgVariant = 'white',
  buttonStyle,
  listStyle,
  mobileContentFirst = false,
  carouselMode = false,
  lottieJson,
  videoEmbedUrl,
  embedCode,
  fullWidthImage = false,
}: CardWithImageProps) => {
  const url = image || '';
  const isLottie = /\.(lottie|json)(\?|$)/i.test(url);
  const isRive = /\.riv(\?|$)/i.test(url);
  const hasMedia = !!url || !!lottieJson || !!videoEmbedUrl || !!embedCode;

  // Use centeredMobile if provided, otherwise fall back to centered
  const mobileCentered = centeredMobile !== undefined ? centeredMobile : centered;
  const desktopCentered = centered;

  return (
    <div
      className={cn(
        styles.card,
        styles[`card--count-${cardCount}`],
        shouldCenterOnTablet && styles['card--tablet-center'],
        styles[`card--bg-${bgVariant}`],
        styles[`card--content-${contentDirection}`],
        styles[`card--title-${(titleStyle ?? titleTag ?? 'H6').toLowerCase()}`],
        imagePosition === 'right' && styles['card--image-right'],
        mobileContentFirst && styles['card--mobile-content-first'],
        mobileCentered && styles['card--centered-mobile'],
        desktopCentered && styles['card--centered-desktop'],
        carouselMode && styles['card--carousel'],
      )}
    >
      {hasMedia && (
        <div
          className={cn(
            styles.card_image,
            styles[`card_image--${imageType}`],
            styles[`card_image--${contentDirection}`],
            fullWidthImage && styles['card_image--full-width'],
          )}
        >
          {embedCode ? (
            <HtmlEmbed code={embedCode} />
          ) : videoEmbedUrl ? (
            <iframe
              src={videoEmbedUrl}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                width: '100%',
                maxWidth: '100%',
                height: 'auto',
                aspectRatio: '16/9',
                border: 0,
              }}
            />
          ) : lottieJson ? (
            <LottieComponent data={lottieJson} className="h-full w-full" />
          ) : isLottie ? (
            <LottieComponent src={url} className="h-full w-full" />
          ) : isRive ? (
            <RiveComponent src={url} className="h-full w-full" />
          ) : (
            <Image src={url} alt={title} width={500} height={500} />
          )}
        </div>
      )}
      <div
        className={cn(
          styles.card_content,
          mobileCentered && styles['card_content--centered-mobile'],
          desktopCentered && styles['card_content--centered-desktop'],
        )}
      >
        <div className={styles.card_content_header}>
          {(() => {
            // const Tag = (titleTag ?? 'H5').toLowerCase() as 'p' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
            return <h3 className={cn(styles.card_title, 'h5')}>{title}</h3>;
          })()}
          <div className={styles.card_description}>
            {typeof description === 'string' ? (
              <p>{description}</p>
            ) : (
              <RichTextViewer content={description} listStyle={listStyle} />
            )}
          </div>
        </div>
        {linkLabel &&
          (type === 'link' ? (
            <Link
              label={linkLabel}
              url={link}
              type="Link"
              buttonStyle={buttonStyle}
              withArrow={true}
              target={newWindow ? '_blank' : undefined}
              rel={newWindow ? 'noopener noreferrer' : undefined}
              destination={destination}
              ctaId={ctaId}
              {...formContent}
              className={styles.card_link}
            />
          ) : destination === 'Form' ? (
            <Link
              label={linkLabel}
              url={link}
              type="Button"
              buttonStyle={buttonStyle}
              withArrow={false}
              target={newWindow ? '_blank' : undefined}
              rel={newWindow ? 'noopener noreferrer' : undefined}
              destination={destination}
              ctaId={ctaId}
              {...formContent}
            />
          ) : (
            <Link
              label={linkLabel}
              url={link}
              type="Button"
              buttonStyle={buttonStyle}
              size="large"
              withArrow={true}
              target={newWindow ? '_blank' : undefined}
              rel={newWindow ? 'noopener noreferrer' : undefined}
              destination={destination}
              ctaId={ctaId}
              {...formContent}
            />
          ))}
      </div>
    </div>
  );
};
